<template>
    <DefaultLayout :meta="meta">

        <div class="w-full bg-gray-50">

            <div class="w-full max-w-screen-xl mx-auto px-6 py-6">

                <h1 class="font-bold text-center">
                    {{ __('home::checkout.billing') }}
                </h1>

            </div>

        </div>

        <div class="w-full max-w-screen-xl mx-auto px-6 py-6">

            <form @submit.prevent="submit">

                <div class="grid grid-cols-1 xl:grid-cols-3 gap-12">

                    <div class="md:py-10">

                        <div class="flex justify-between border-b pb-4 mb-6">
                            <h2 class="font-semibold text-2xl">1. {{ __('home::checkout.delivery_address') }}</h2>
                        </div>

                        <FormGroup :label="__('admin::labels.name')" :error="errors.customer_name">
                            <Input name="customer_name" type="text" :has-error="exists(errors.customer_name)" v-model="form.customer_name" :required="true" autocomplete="name" />
                        </FormGroup>

                        <FormGroup :label="__('admin::labels.address')" :error="errors.customer_address" class="grid-span-1">
                            <Input name="customer_address" type="text" :has-error="exists(errors.customer_address)" v-model="form.customer_address" :required="true" @change="updateServicepoints" />
                        </FormGroup>

                        <div class="grid grid-cols-3 gap-4">

                            <FormGroup :label="__('admin::labels.zipcode')" :error="errors.customer_zipcode" class="grid-span-1">
                                <Input name="customer_zipcode" type="text" :has-error="exists(errors.customer_zipcode)" v-model="form.customer_zipcode" :required="true" @change="updateServicepoints" />
                            </FormGroup>

                            <FormGroup :label="__('admin::labels.city')" :error="errors.customer_city" class="col-span-2">
                                <Input name="customer_city" type="text" :has-error="exists(errors.customer_city)" v-model="form.customer_city" :required="true" @change="updateServicepoints" />
                            </FormGroup>

                        </div>

                        <FormGroup :label="__('admin::labels.country')" :error="errors.customer_country">
                            <Select name="customer_country" :options="countryOptions" :has-error="exists(errors.customer_country)" v-model="form.customer_country" @change="updateServicepoints" :required="true" />
                        </FormGroup>

                        <FormGroup :label="__('admin::labels.email')" :error="errors.customer_email">
                            <Input name="customer_email" type="email" :has-error="exists(errors.customer_email)" v-model="form.customer_email" :required="true" />
                        </FormGroup>

                        <FormGroup :label="__('admin::labels.phone')" :error="errors.customer_phone">
                            <Input name="customer_phone" type="tel" :has-error="exists(errors.customer_phone)" v-model="form.customer_phone" :required="true" />
                        </FormGroup>

                    </div>

                    <div class="md:py-10">

                        <h2 class="text-2xl font-bold pb-4 mb-6 border-b">2. {{ __('home::checkout.delivery_method') }}</h2>

                        <DeliveryMethods
                            ref="deliveryMethods"
                            :form="form"
                            v-model:order="order"
                            v-model:method="form.delivery_method_id"
                            v-model:servicepoint="form.delivery_servicepoint_id"
                        />

                    </div>

                    <div class="px-8 py-10 bg-gray-50">

                        <h2 class="text-2xl font-bold pb-4 mb-6 border-b">3. {{ __('home::checkout.payment') }}</h2>

                        <div>
                            <div class="flex justify-between py-1 text-sm uppercase" v-for="line in groupedOrderLines">
                                <div class="flex items-center gap-2">
                                    <span>{{ line.quantity }} x {{ line.product.name }}</span>
                                    <div
                                        class="px-2 py-0.5 text-xs rounded-full shadow-sm font-bold text-center"
                                        :class="[$page.props.__site.promotion_style === 'black_friday'
                                            ? 'text-black bg-[#ffd732]'
                                            : 'text-primary bg-primary-light']"
                                        v-if="$page.props.__site.promotion_product_line"
                                        v-html="$page.props.__site.promotion_product_line"
                                    />
                                </div>
                                <span>{{ formatPrice(line.total, order.currency) }}</span>
                            </div>

                            <div class="mt-3" v-if="order.delivery_method || order.discount > 0">
                                <div class="flex justify-between py-1 text-sm uppercase" v-if="order.delivery_method">
                                    <span>{{ __('home::checkout.delivery') }}</span>
                                    <span>{{ formatPrice(order.delivery_method.prices[order.currency] * (1 + order.vat_rate), order.currency) }}</span>
                                </div>
                                <div class="flex justify-between py-1 text-sm uppercase" v-if="order.discount > 0">
                                    <span>{{ __('home::checkout.discount') }}</span>
                                    <span>-{{ formatPrice(order.discount, order.currency) }}</span>
                                </div>
                            </div>

                            <div class="mt-3">
                                <!--
                                <div class="flex justify-between py-1 text-sm uppercase">
                                    <span>{{ __('home::checkout.subtotal') }}</span>
                                    <span>{{ formatPrice(order.subtotal, order.currency) }}</span>
                                </div>
                                <div class="flex justify-between py-1 text-sm uppercase">
                                    <span>{{ __('home::checkout.vat') }}</span>
                                    <span>{{ formatPrice(order.vat, order.currency) }}</span>
                                </div>
                                -->
                                <div class="flex font-semibold justify-between py-1 text-sm uppercase">
                                    <span>{{ __('home::checkout.total_incl_vat') }}</span>
                                    <span>{{ formatPrice(order.total, order.currency) }}</span>
                                </div>
                            </div>
                        </div>

                        <div class="text-gray-600 my-8">{{ __('home::checkout.payment_info') }}</div>

                        <PaymentMethods
                            v-model:method="form.payment_method"
                            class="mb-8"
                        />

                        <FormGroup :error="errors.newsletter" class="pb-0">
                            <Checkbox name="newsletter" v-model:checked="form.newsletter">
                                {{ __('home::checkout.sign_up_newsletter') }}
                            </Checkbox>
                        </FormGroup>

                        <FormGroup :error="errors.consent">
                            <Checkbox name="consent" v-model:checked="form.consent">
                                {{ __('home::checkout.i_give_consent') }} <br>
                                <a href="/betingelser" target="_blank" class="text-primary text-sm">{{ __('home::checkout.read_terms') }}</a>
                            </Checkbox>
                        </FormGroup>

                        <div class="px-4 py-2 rounded-sm shadow-sm text-xs lg:text-sm text-primary bg-primary-light text-center mb-3" v-if="delayedPaymentDays">
                            {{ __('home::orders.pay_later_terms', { days: delayedPaymentDays }) }}
                        </div>

                        <Button type="submit" class="w-full text-lg py-3 px-6">
                            {{ __('home::checkout.go_to_payment') }}
                            <ArrowNarrowRightIcon class="h-6 w-6 ml-1" />
                        </Button>

                    </div>

                </div>

            </form>

        </div>

    </DefaultLayout>
</template>

<script>
    import axios from 'axios'
    import collect from 'collect.js'

    import {
        ArrowNarrowRightIcon,
        ChevronRightIcon,
    } from '@heroicons/vue/solid'

    export default {
        components: {
            ArrowNarrowRightIcon,
            ChevronRightIcon,
        },
        props: {
            errors: Object,
            meta: Object,
            initialOrder: Object,
            countryOptions: Object,
            dataLayer: Object,
            delayedPaymentDays: Number|String,
        },
        data() {
            return {
                order: this.initialOrder,
                groupedOrderLines: collect(this.initialOrder.lines)
                    .groupBy('product_id')
                    .map((group) => {
                        return {
                            product: group.first().product,
                            quantity: group.sum('quantity'),
                            total: group.sum('total'),
                        };
                    })
                    .all(),
                form: this.$inertia.form({
                    customer_email: this.initialOrder.customer_email || '',
                    customer_phone: this.initialOrder.customer_phone || '',
                    customer_name: this.initialOrder.customer_name || '',
                    customer_address: this.initialOrder.customer_address || '',
                    customer_zipcode: this.initialOrder.customer_zipcode || '',
                    customer_city: this.initialOrder.customer_city || '',
                    customer_country: this.initialOrder.customer_country ? this.initialOrder.customer_country : this.getDefaultCountry(),
                    delivery_method_id: this.initialOrder.delivery_method_id,
                    delivery_servicepoint_id: this.initialOrder.delivery_servicepoint_id,
                    consent: false,
                    newsletter: false,
                    payment_method: 'card',
                }),
            }
        },
        mounted() {
            this.$gtm.trackEvent(this.dataLayer);
        },
        methods: {
            updateServicepoints() {
                this.$refs.deliveryMethods.getServicepoints();
            },
            getDefaultCountry() {
                return this.initialOrder.site.country;
            },
            submit() {
                let dataLayer = this.dataLayer;
                dataLayer.event = 'GoToPayment';

                this.$gtm.trackEvent(dataLayer);

                this.form
                    .post(this.$route('home::checkout.go-to-payment'));
            },
        },
    }
</script>
